import { useState, useContext, useEffect } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { TourContext, type TourGuide } from '../../contexts/TourContext';
import { ColorModeContext } from '../../App';

const categoryIcons: Record<string, string> = {
  'getting-started': '🚀',
  'daily-operations': '📋',
  'administration': '⚙️',
  'advanced': '🎓',
};

const categoryLabels: Record<string, string> = {
  'getting-started': 'Getting Started',
  'daily-operations': 'Daily Operations',
  'administration': 'Administration',
  'advanced': 'Advanced Features',
};

export function TourMenuButton() {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const tourContext = useContext(TourContext);
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  if (!tourContext) {
    console.error('TourMenuButton must be used within TourProvider');
    return null;
  }

  const {
    tourGuides,
    startTour,
    tourProgress,
    resetTourProgress,
    getCompletionPercentage,
  } = tourContext;

  // Listen for custom event to open tour menu (triggered by ? keyboard shortcut)
  useEffect(() => {
    const handleOpenMenu = () => setOpen(true);
    window.addEventListener('open-tour-menu', handleOpenMenu);
    return () => window.removeEventListener('open-tour-menu', handleOpenMenu);
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSearchQuery('');
  };

  const handleStartTour = (guideId: string) => {
    handleClose();
    startTour(guideId);
  };

  const handleResetProgress = () => {
    if (window.confirm('Are you sure you want to reset all tour progress?')) {
      resetTourProgress();
    }
  };

  // Filter tours by search query
  const guidesArray = Array.from(tourGuides.values());
  const filteredGuides = searchQuery
    ? guidesArray.filter(
        (guide) =>
          guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          guide.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : guidesArray;

  // Group by category
  const guidesByCategory = filteredGuides.reduce((acc, guide) => {
    if (!acc[guide.category]) {
      acc[guide.category] = [];
    }
    acc[guide.category].push(guide);
    return acc;
  }, {} as Record<string, TourGuide[]>);

  const completionPercentage = getCompletionPercentage();
  const completedCount = Object.values(tourProgress).filter((p) => p.completed).length;
  const totalCount = tourGuides.size;

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip title="Interactive Tours (Press ?)">
        <Badge
          badgeContent={completionPercentage === 100 ? <TrophyIcon fontSize="small" /> : `${completionPercentage}%`}
          color={completionPercentage === 100 ? 'success' : 'primary'}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
          }}
        >
          <Fab
            color="primary"
            aria-label="tours"
            onClick={handleOpen}
            sx={{
              boxShadow: 3,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s',
              },
            }}
          >
            <HelpIcon />
          </Fab>
        </Badge>
      </Tooltip>

      {/* Tour Menu Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <HelpIcon color="primary" />
              <Typography variant="h6">Interactive Tours</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Reset Progress">
                <IconButton onClick={handleResetProgress} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                Tour Progress
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {completedCount} / {totalCount} Completed
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {completionPercentage === 100 && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <TrophyIcon color="success" />
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  🎉 Congratulations! You've completed all tours!
                </Typography>
              </Box>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Search Box */}
          <TextField
            fullWidth
            placeholder="Search tours..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ mb: 2 }}
          />

          {/* Help Text */}
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 1,
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
          }}>
            <Typography variant="body2" color="text.secondary" component="div">
              💡 <strong>Tip:</strong> Press <Chip label="?" size="small" sx={{ mx: 0.5 }} /> anywhere in the app to open this menu.
              Start with the "Getting Started" tours if you're new!
            </Typography>
          </Box>

          {/* Tours List by Category */}
          {Object.keys(guidesByCategory).length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No tours found matching "{searchQuery}"</Typography>
            </Box>
          ) : (
            Object.entries(guidesByCategory).map(([category, guides]) => (
              <Box key={category} mb={3}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <span>{categoryIcons[category]}</span>
                  {categoryLabels[category]}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List disablePadding>
                  {guides.map((guide) => {
                    const isCompleted = tourProgress[guide.id]?.completed;
                    const completionCount = tourProgress[guide.id]?.completionCount || 0;

                    return (
                      <ListItem key={guide.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleStartTour(guide.id)}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon>
                            {isCompleted ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <UncheckedIcon color="action" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1">{guide.title}</Typography>
                                {guide.estimatedMinutes && (
                                  <Chip
                                    label={`${guide.estimatedMinutes} min`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {completionCount > 1 && (
                                  <Chip
                                    label={`×${completionCount}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={guide.description}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ))
          )}

          {/* Footer Help */}
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light', 
            borderRadius: 1,
            border: isDark ? '1px solid rgba(99, 102, 241, 0.3)' : 'none'
          }}>
            <Typography variant="body2" color={isDark ? 'primary.light' : 'primary.contrastText'}>
              <strong>Need more help?</strong> Click the help icon in the header for contextual guidance, or contact support.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
